import React, { useMemo } from 'react';
import {
  AccordionsItem,
  AccordionsItemHeader,
  AccordionsItemSection,
  backgroundColorNames,
  Badge,
  Flashy,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  LayoutItem,
  Paragraph,
  ParagraphHeader,
  ReadonlyTextarea,
  SectionHeader,
  SmallHeader,
  Visible,
  Well,
} from '@axiom/ui';
import type { CandidateOpportunityOnboarding } from '@axiom/validation';
import type { UseOnboardingTextReturn, BadgeBackgroundType } from '@axiom/ui';
import { CountryCodesConst, CandidateOpportunitiesConst } from '@axiom/const';
import { CurrencyUtil } from '@axiom/utils';

import type { Nullable } from '../../types/utility.types';

export type OnboardingCompBenefitsProps = {
  name?: string;
  position:
    | NonNullable<CandidateOpportunityOnboarding['positions']>[number]
    | undefined;
  texts: Nullable<
    Pick<
      NonNullable<ReturnType<UseOnboardingTextReturn['getOnboardingTexts']>>,
      'benefits' | 'holiday'
    >
  >;
};
const { CountryCodesAbbreviations: CountryCodeAbbr } = CountryCodesConst;
const TimeUnit = {
  HOURLY: 'Hourly',
  DAILY: 'Daily',
} as const;

export const EngagementDetailsCompBenefits = ({
  name,
  position,
  texts,
}: OnboardingCompBenefitsProps) => {
  const timeUnit = useMemo(() => {
    const onboardingDetails =
      position?.candidateOpportunities?.[0]?.onboardingDetails ?? null;
    const countryCodes = [CountryCodeAbbr.US, CountryCodeAbbr.CA];
    const displayTimeUnit = onboardingDetails?.displayTimeUnit ?? null;

    if (displayTimeUnit) return displayTimeUnit;

    return countryCodes.includes(
      position?.candidateOpportunities?.[0]?.candidate.addressCountryCode
    )
      ? TimeUnit.HOURLY
      : TimeUnit.DAILY;
  }, [position?.candidateOpportunities?.[0]?.onboardingDetails]);

  const rateDisplay = useMemo(() => {
    const hourly =
      position?.candidateOpportunities?.[0]?.proposedHourlyCompensation ?? 1;

    if (timeUnit === TimeUnit.DAILY) {
      const daily = hourly * (position?.billingHoursPerDay ?? 0);
      return `${CurrencyUtil.getFormattedCurrency({ value: daily }) ?? '$0'} per day`;
    }

    return `${CurrencyUtil.getFormattedCurrency({ value: hourly }) ?? '$0'} per hour`;
  }, [position, timeUnit]);

  const getCompensationBadge = () => {
    const compensationType =
      position?.candidateOpportunities?.[0]?.compensationType ?? null;
    const { EngagementSpecific, Premium } =
      CandidateOpportunitiesConst.CompensationTypes;
    let color: BadgeBackgroundType = backgroundColorNames.sky;
    let text: string;

    if (compensationType === EngagementSpecific) {
      text = EngagementSpecific;
    } else if (compensationType === Premium) {
      color = backgroundColorNames.green;
      text = Premium;
    } else {
      return null;
    }

    return (
      <Badge name="COMPENSATION_BADGE" background={color}>
        {text}
      </Badge>
    );
  };

  return (
    <AccordionsItem name={name}>
      <AccordionsItemHeader>
        <SmallHeader>Compensation & Benefits</SmallHeader>
      </AccordionsItemHeader>
      <AccordionsItemSection>
        <Gutter top="24px" />
        <Well>
          <Layout
            direction="horizontal"
            reversibleOn="mobile"
            stackableOn="mobile"
          >
            <LayoutItem fluid>
              <SectionHeader>Compensation</SectionHeader>
            </LayoutItem>
            <LayoutItem>{getCompensationBadge()}</LayoutItem>
          </Layout>
          <Gutter bottom="24px" />
          <Grid stackable>
            <GridRow columns={2} gutterBottom="16px">
              <GridColumn>
                <Layout stackableOn="mobile">
                  <LayoutItem rightGutter="8px">
                    <ParagraphHeader>Type of compensation:</ParagraphHeader>
                  </LayoutItem>
                  <LayoutItem name="RESERVED_TYPE">
                    {position?.reservedType || '--'}
                  </LayoutItem>
                </Layout>
                <Visible only="mobile">
                  <Gutter bottom="8px" />
                </Visible>
              </GridColumn>
              <GridColumn>
                <Layout stackableOn="mobile">
                  <LayoutItem rightGutter="8px">
                    <ParagraphHeader>Annual Base Compensation:</ParagraphHeader>
                  </LayoutItem>
                  <LayoutItem name="BASE_ANNUAL_SALARY">
                    {position?.candidateOpportunities?.[0]?.candidate
                      .baseAnnualSalary
                      ? CurrencyUtil.getFormattedCurrency({
                          value:
                            position.candidateOpportunities[0].candidate
                              .baseAnnualSalary,
                        })
                      : '--'}
                  </LayoutItem>
                </Layout>
              </GridColumn>
            </GridRow>
            <GridRow>
              <GridColumn>
                <Well name="RATE_DISPLAY" background="light">
                  The rate for this engagement on{' '}
                  {timeUnit === TimeUnit.HOURLY ? 'an ' : 'a '}
                  <Flashy bold>{timeUnit}</Flashy> basis is{' '}
                  <Flashy bold>{rateDisplay}</Flashy>
                </Well>
              </GridColumn>
            </GridRow>
          </Grid>
        </Well>
        {texts.benefits ? (
          <Gutter top="24px">
            <Well>
              <SectionHeader>Benefits</SectionHeader>
              <Gutter bottom="16px" />
              <Paragraph>
                <ReadonlyTextarea
                  name="benefits"
                  value={texts.benefits}
                  formatting="modern"
                />
              </Paragraph>
            </Well>
          </Gutter>
        ) : null}
        {texts.holiday ? (
          <Gutter top="24px">
            <Well>
              <SectionHeader>Holiday</SectionHeader>
              <Gutter bottom="16px" />
              <Paragraph>
                <ReadonlyTextarea
                  name="holiday"
                  value={texts.holiday}
                  formatting="modern"
                />
              </Paragraph>
            </Well>
          </Gutter>
        ) : null}
      </AccordionsItemSection>
    </AccordionsItem>
  );
};
